import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Smart CRM — marketing campaigns (Step 7). Backend: MarketingController, every
 * call scoped to the authed company + gated by marketing_email.
 *
 *   GET    /marketing/quota                     per-channel usage snapshot
 *   GET    /marketing/campaigns?search&status&page&per_page → paginator
 *   POST   /marketing/campaigns                 create (send_mode: draft|now|later)
 *   GET    /marketing/campaigns/{id}            single
 *   PUT    /marketing/campaigns/{id}            edit (draft/scheduled only)
 *   DELETE /marketing/campaigns/{id}            cancel + soft delete
 *   POST   /marketing/campaigns/preflight       audience + quota math (no write)
 *   POST   /marketing/campaigns/{id}/test       one test copy to venue inbox
 *
 * List returns the full paginator (for the lazy DataTable). Single/quota/preflight
 * unwrap `res?.data ?? res` (CompanyResource wraps in { data }).
 */

const LIST_KEY  = 'campaigns';
const QUOTA_KEY  = 'marketingQuota';

const unwrap = (res) => res?.data ?? res;

export const useGetMarketingQuota = () =>
  useQuery(
    QUOTA_KEY,
    async () => unwrap((await axiosInstance.get('/marketing/quota')).data),
    { staleTime: 15_000 }
  );

export const useGetCampaigns = (params = {}) =>
  useQuery(
    [LIST_KEY, params],
    async () => (await axiosInstance.get('/marketing/campaigns', { params })).data,
    { keepPreviousData: true, staleTime: 10_000 }
  );

export const useCreateCampaign = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    async (payload) => unwrap((await axiosInstance.post('/marketing/campaigns', payload)).data),
    {
      onSuccess: () => { qc.invalidateQueries(LIST_KEY); qc.invalidateQueries(QUOTA_KEY); },
      onError: (err) => showToast('error', 'Marketing', err.response?.data?.message ?? 'Could not save campaign.'),
    }
  );
};

export const useUpdateCampaign = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    async ({ id, ...payload }) => unwrap((await axiosInstance.put(`/marketing/campaigns/${id}`, payload)).data),
    {
      onSuccess: () => { qc.invalidateQueries(LIST_KEY); qc.invalidateQueries(QUOTA_KEY); },
      onError: (err) => showToast('error', 'Marketing', err.response?.data?.message ?? 'Could not update campaign.'),
    }
  );
};

export const useCancelCampaign = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/marketing/campaigns/${id}`), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); },
    onError: (err) => showToast('error', 'Marketing', err.response?.data?.message ?? 'Could not cancel campaign.'),
  });
};

/** Audience + quota preflight. POST (computes counts), so a mutation. */
export const usePreflight = () =>
  useMutation(async (body) => unwrap((await axiosInstance.post('/marketing/campaigns/preflight', body)).data));

/* ── Step 9: Insights (read-only) ──────────────────────────────────────── */
export const useGetInsights = () =>
  useQuery(
    'marketingInsights',
    async () => unwrap((await axiosInstance.get('/marketing/insights')).data),
    { staleTime: 10_000 }
  );

export const useGetMessages = (params = {}) =>
  useQuery(
    ['marketingMessages', params],
    async () => (await axiosInstance.get('/marketing/messages', { params })).data,
    { keepPreviousData: true, staleTime: 10_000 }
  );

/* ── Step 8: always-on auto campaigns ──────────────────────────────────── */
const AUTO_KEY = 'automations';

/** The 3 automations (create-if-missing on the backend). */
export const useGetAutomations = () =>
  useQuery(
    AUTO_KEY,
    async () => unwrap((await axiosInstance.get('/marketing/automations')).data),
    { staleTime: 10_000 }
  );

export const useUpdateAutomation = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    async ({ id, ...payload }) => unwrap((await axiosInstance.put(`/marketing/automations/${id}`, payload)).data),
    {
      onSuccess: () => qc.invalidateQueries(AUTO_KEY),
      onError: (err) => showToast('error', 'Marketing', err.response?.data?.message ?? 'Could not save automation.'),
    }
  );
};

/* ── Step 10: Analytics + Suggestions (read-only) ──────────────────────── */
export const useGetAnalytics = () =>
  useQuery(
    'marketingAnalytics',
    async () => unwrap((await axiosInstance.get('/marketing/analytics')).data),
    { staleTime: 60_000 }
  );

export const useGetSuggestions = () =>
  useQuery(
    'marketingSuggestions',
    async () => unwrap((await axiosInstance.get('/marketing/suggestions')).data),
    { staleTime: 60_000 }
  );

/* ── Step 13: Activity Insights (read-only) ─────────────────────────────── */

export const useGetCelebrations = (params = {}) =>
  useQuery(
    ['marketingCelebrations', params],
    async () => unwrap((await axiosInstance.get('/marketing/activity/celebrations', { params })).data),
    { staleTime: 30_000, keepPreviousData: true }
  );

export const useGetSpinActivity = (params = {}) =>
  useQuery(
    ['marketingSpinActivity', params],
    async () => unwrap((await axiosInstance.get('/marketing/activity/spin', { params })).data),
    { staleTime: 30_000, keepPreviousData: true }
  );

/** Send one test copy to the venue's own inbox. */
export const useSendTest = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async (id) => unwrap((await axiosInstance.post(`/marketing/campaigns/${id}/test`)).data),
    {
      onError: (err) => showToast('error', 'Marketing', err.response?.data?.message ?? 'Test send failed.'),
    }
  );
};
